import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'en_espera_cliente', label: 'En espera del cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' },
];

const PRIORITY_STYLES = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const AdminClientService = () => {
  const { clientId } = useParams();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const q = query(
          collection(db, 'clients', clientId, 'serviceTickets'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setTickets(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (err) {
        console.error('❌ Error cargando tickets del cliente:', err);
      }
    };
    fetchTickets();
  }, [clientId]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const q = query(
        collection(db, 'clients', clientId, 'serviceTickets', ticket.id, 'timeline'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setTimeline(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error('❌ Error cargando timeline:', err);
    }
  };

  const addEntry = async () => {
    if (!selectedTicket || !note.trim()) return;
    try {
      await addDoc(collection(db, 'clients', clientId, 'serviceTickets', selectedTicket.id, 'timeline'), {
        summary: note.trim(),
        channel: 'admin',
        entityType: 'serviceTicket',
        ticketTitle: selectedTicket.title,
        clientName: selectedTicket.clientName,
        createdBy: currentUser?.email || 'admin',
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'clients', clientId, 'serviceTickets', selectedTicket.id), {
        updatedAt: serverTimestamp(),
      });
      setNote('');
      openTicket(selectedTicket);
    } catch (err) {
      console.error('❌ Error agregando nota:', err);
    }
  };

  const changeStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      await updateDoc(doc(db, 'clients', clientId, 'serviceTickets', selectedTicket.id), {
        status,
        updatedAt: serverTimestamp(),
      });
      setTickets((prev) => prev.map((t) => (t.id === selectedTicket.id ? { ...t, status } : t)));
      setSelectedTicket((prev) => prev && { ...prev, status });
    } catch (err) {
      console.error('❌ Error actualizando estado:', err);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Servicio del cliente</h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow border">
          <ul className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {tickets.map((ticket) => (
              <li
                key={ticket.id}
                className={`px-4 py-3 cursor-pointer ${
                  selectedTicket?.id === ticket.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => openTicket(ticket)}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-900">{ticket.title}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_STYLES[ticket.priority] || 'bg-gray-100'}`}>
                    {ticket.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}
                </p>
              </li>
            ))}
            {tickets.length === 0 && (
              <li className="px-4 py-6 text-sm text-gray-500">Sin tickets registrados.</li>
            )}
          </ul>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          {selectedTicket ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-gray-500">Ticket</p>
                  <h4 className="text-lg font-semibold text-gray-900">{selectedTicket.title}</h4>
                </div>
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={selectedTicket.status}
                  onChange={(e) => changeStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Actualizaciones</h5>
                {timeline.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin notas todavía.</p>
                ) : (
                  <ul className="space-y-3 max-h-60 overflow-y-auto">
                    {timeline.map((entry) => (
                      <li key={entry.id} className="border rounded px-3 py-2 text-sm">
                        <p className="text-gray-800">{entry.summary}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {entry.createdBy || entry.channel} ·{' '}
                          {entry.createdAt?.toDate
                            ? entry.createdAt
                                .toDate()
                                .toLocaleString('es-PR', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  month: 'short',
                                  day: 'numeric',
                                })
                            : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <textarea
                  rows={3}
                  className="border rounded w-full px-3 py-2 text-sm"
                  placeholder="Añade una nota"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  type="button"
                  onClick={addEntry}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Registrar nota
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Selecciona un ticket para ver los detalles.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClientService;
